import { z } from 'zod';
import { costValueSchema } from '../../pricing/cost-value.js';

// ─── ai.preview ───────────────────────────────────────────────────────────────

export const aiPreviewRequestedSchema = z.object({
  prompt_id: z.number().int().positive(),
  wallpaper_id: z.number().int().positive(),
});

export const aiPreviewRespondedSchema = z.union([
  z.object({
    rendered_system: z.string(),
    rendered_user: z.string(),
    vars: z.record(z.unknown()),
    warnings: z.array(z.string()),
  }),
  z.object({
    error: z.string(),
  }),
]);

// ─── ai.testrun ───────────────────────────────────────────────────────────────

export const aiTestrunRequestedSchema = z.object({
  prompt_id: z.number().int().positive(),
  wallpaper_id: z.number().int().positive(),
  user_id: z.string().min(1),
  /** Override the prompt's configured model by ai_models.id. The handler
   *  resolves the row, takes its provider_code + model_id + tariff, and uses
   *  those instead of the prompt's pinned model. Skip when you just want to
   *  run the prompt as it's configured. */
  ai_model_id: z.number().int().positive().optional(),
  /** Routes the request through a workflow-specific bypass for safeguards.
   *  'compare' skips the per-user rate limiter (the compare UI sends many runs
   *  in burst, vs the prompt editor's tight 5/hour ceiling). */
  kind: z.enum(['edit', 'compare']).optional(),
  /** Override the image URL used for describe. Required when the caller
   *  wants a real LLM call against a locally-hosted Supabase asset (otherwise
   *  preview-vars synthesises a `cdn.wandero.pl/...` URL that the Gateway
   *  can't reach in dev). */
  image_url: z.string().url().optional(),
});

export const aiTestrunRespondedSchema = z.union([
  z.object({
    output: z.unknown(),
    metrics: z.object({
      tokensIn: z.number().int().nonnegative(),
      tokensOut: z.number().int().nonnegative(),
      cost: costValueSchema,
      model: z.string(),
      latencyMs: z.number().int().nonnegative(),
    }),
    prompt_id: z.string(),
  }),
  z.object({
    error: z.string(),
    resetAt: z.number().int().positive().optional(),
  }),
]);

// ─── Payload types ────────────────────────────────────────────────────────────

export type AiPreviewRequestedPayload = z.infer<typeof aiPreviewRequestedSchema>;
export type AiPreviewRespondedPayload = z.infer<typeof aiPreviewRespondedSchema>;
export type AiTestrunRequestedPayload = z.infer<typeof aiTestrunRequestedSchema>;
export type AiTestrunRespondedPayload = z.infer<typeof aiTestrunRespondedSchema>;
